import { X } from 'lucide-react';
import { type ReactElement, type ReactNode, useEffect, useId, useRef } from 'react';
import { cn } from '@/shared/ui';

interface EditorDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  /** 아래쪽 버튼 줄 — 확인·취소 등. */
  readonly footer?: ReactNode;
  readonly className?: string;
}

/**
 * 에디터의 작은 모달 — 디자인 안내·수량별 가격·처음으로 확인에 쓴다. 각진 흰 판에 제목 줄을 얇은 선으로 나눈다.
 * 열리면 닫기 버튼으로 포커스를 옮기고, Escape·바깥 누르기로 닫으며 닫힐 때 원래 포커스로 돌아간다.
 */
export const EditorDialog = ({
  open,
  title,
  onClose,
  children,
  footer,
  className,
}: EditorDialogProps): ReactElement | null => {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="닫기"
        tabIndex={-1}
        className="absolute inset-0 cursor-default bg-black/50"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          'relative w-full max-w-md border-t-2 border-foreground bg-background text-foreground shadow-2xl',
          className,
        )}
      >
        <div className="flex items-center justify-between gap-4 border-b border-foreground/10 px-6 py-4">
          <h2 id={titleId} className="text-lg font-bold tracking-[-0.02em]">
            {title}
          </h2>
          <button
            ref={closeRef}
            type="button"
            aria-label="닫기"
            className="-mr-2 inline-flex size-9 cursor-pointer items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={onClose}
          >
            <X aria-hidden className="size-5" strokeWidth={1.5} />
          </button>
        </div>
        <div className="max-h-[70dvh] overflow-y-auto px-6 py-5 text-sm">{children}</div>
        {footer !== undefined && (
          <div className="flex justify-end gap-2 border-t border-foreground/10 px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};
